/**
 * Web Components for Cart Drawer Enhancements
 * Ported from BR_Nutri_Cure with refinements for drjs (Zest theme)
 */

class CartTieredDiscount extends HTMLElement {
  constructor() {
    super();
    this.container = this.querySelector(".cart-tiered-discount");
  }

  connectedCallback() {
    this.render();
    // Subscribe to cart updates in Zest theme
    if (window.FoxThemeEvents) {
      window.FoxThemeEvents.subscribe("ON_CART_UPDATED", (state) => {
        this.setAttribute(
          "current-total",
          (state.total_price / 100).toString(),
        );
        this.render();
      });
    }
  }

  static get observedAttributes() {
    return ["current-total", "upcoming-message"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  get tiers() {
    try {
      const scriptTag = this.querySelector('script[type="application/json"]');
      const tiers = scriptTag ? JSON.parse(scriptTag.textContent) : [];
      return tiers.sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount));
    } catch (e) {
      console.error("Failed to parse tiers JSON", e);
      return [];
    }
  }

  render() {
    const currentTotal = parseFloat(this.getAttribute("current-total")) || 0;
    const upcomingMessageTemplate =
      this.getAttribute("upcoming-message") ||
      "[amount] away from [next_tier]!";
    const tiers = this.tiers;

    if (tiers.length === 0) return;

    // Find Max Tier
    const maxTierAmount = Math.max(...tiers.map((t) => parseFloat(t.amount)));

    // Find Next Tier
    const nextTier = tiers.find((t) => parseFloat(t.amount) > currentTotal);

    // Calculate Progress %
    const progressPercent = Math.min((currentTotal / maxTierAmount) * 100, 100);

    // Update Message
    const messageContainer = this.querySelector(
      ".cart-tiered-discount__message",
    );
    if (messageContainer) {
      if (nextTier) {
        const amountLeft = parseFloat(nextTier.amount) - currentTotal;
        const amountHtml = `<strong>${this.formatMoney(amountLeft * 100)}</strong>`;
        let message = upcomingMessageTemplate
          .replace("[amount]", amountHtml)
          .replace("[next_tier]", nextTier.label);
        messageContainer.innerHTML = `<p>${message}</p>`;
      } else {
        const lastTierMessage =
          tiers[tiers.length - 1].cart_message ||
          "You've unlocked all rewards!";
        messageContainer.innerHTML = `<p>${lastTierMessage}</p>`;
      }
    }

    // Update Progress Bar
    const progressBar = this.querySelector(".cart-tiered-discount__progress");
    if (progressBar) {
      progressBar.style.width = `${progressPercent}%`;
    }

    // Update Nodes
    const nodes = this.querySelectorAll(".cart-tiered-discount__node");
    nodes.forEach((node, index) => {
      const threshold = parseFloat(tiers[index].amount);
      if (currentTotal >= threshold) {
        node.classList.add("active");
      } else {
        node.classList.remove("active");
      }

      // Handle last node class
      if (index === nodes.length - 1) {
        node.classList.add("last-node");
      } else {
        node.classList.remove("last-node");
      }
    });
  }

  formatMoney(cents) {
    if (window.FoxThemeSettings && window.FoxThemeSettings.money_format) {
      // Simple formatter if custom format is needed, otherwise use Shopify.formatMoney
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(
          cents,
          window.FoxThemeSettings.money_format,
        );
      }
    }
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return "₹" + (cents / 100).toFixed(2);
  }
}

if (!customElements.get("cart-tiered-discount")) {
  customElements.define("cart-tiered-discount", CartTieredDiscount);
}

class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", this.onItemAdd.bind(this));
  }

  onItemAdd(event) {
    const addBtn = event.target.closest(".cart-drawer-upsell__add-btn");
    if (!addBtn) return;

    const variantId = addBtn.dataset.variantId;
    if (!variantId) return;

    addBtn.classList.add("loading");
    addBtn.disabled = true;
    const originalText = addBtn.innerHTML;
    addBtn.innerHTML = "Adding...";

    const formData = {
      items: [
        {
          id: variantId,
          quantity: 1,
        },
      ],
      sections: "cart-drawer", // Request the cart-drawer section to update
    };

    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((response) => {
        // Trigger cart refresh using Zest's Cart component if available
        if (
          window.FoxTheme &&
          window.FoxTheme.Cart &&
          typeof window.FoxTheme.Cart.renderContents === "function"
        ) {
          window.FoxTheme.Cart.renderContents(response);
          // Explicitly emit event if needed
          if (window.FoxThemeEvents) {
            window.FoxThemeEvents.emit("ON_ITEM_ADDED", response);
          }
        } else {
          window.location.reload();
        }
      })
      .catch((error) => {
        console.error("Error adding to cart:", error);
        addBtn.innerHTML = "Error";
      })
      .finally(() => {
        // Small delay to allow renderContents to potentially replace the DOM
        // if it hasn't, we reset the button
        setTimeout(() => {
          if (this.contains(addBtn)) {
            addBtn.innerHTML = originalText;
            addBtn.disabled = false;
            addBtn.classList.remove("loading");
          }
        }, 500);
      });
  }
}

if (!customElements.get("cart-upsell")) {
  customElements.define("cart-upsell", CartUpsell);
}
